import React, { useState, useEffect } from 'react';
import api from '../services/api';
import assessmentService from '../services/assessmentService';
import useTenant from '../hooks/useTenant';
import type { ResultAnalysisReport } from '../types';

interface ClassItem {
  id: number;
  class_name: string;
}

interface SubjectItem {
  id: number;
  subject_name: string;
}

interface AcademicYearItem {
  id: number;
  year_name: string;
}

export const Reports: React.FC = () => {
  const { tenantName } = useTenant();
  
  // Lists fetched dynamically
  const [classesList, setClassesList] = useState<ClassItem[]>([]);
  const [subjectsList, setSubjectsList] = useState<SubjectItem[]>([]);
  const [academicYears, setAcademicYears] = useState<AcademicYearItem[]>([]);

  // Roster parameters state
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('1_1');
  const [selectedYear, setSelectedYear] = useState<string>('');

  const [report, setReport] = useState<ResultAnalysisReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch metadata lists on mount
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [classesRes, subjectsRes, formRes] = await Promise.all([
          api.get('/classes'),
          api.get('/subjects'),
          api.get('/manage-classes/form-data')
        ]);

        const classesData = classesRes.data.success ? classesRes.data.data : [];
        const subjectsData = subjectsRes.data.success ? subjectsRes.data.data : [];
        const yearsData = formRes.data.success ? formRes.data.data.academic_years : [];

        setClassesList(classesData);
        setSubjectsList(subjectsData);
        setAcademicYears(yearsData);

        if (classesData.length > 0) setSelectedClass(String(classesData[0].id));
        if (subjectsData.length > 0) setSelectedSubject(String(subjectsData[0].id));
        if (yearsData.length > 0) setSelectedYear(String(yearsData[0].id));

      } catch (err: any) {
        setError('Failed to load class/subject listing options. Please verify your connection.');
      } finally {
        setMetaLoading(false);
      }
    };

    fetchMetadata();
  }, []);

  const fetchReport = async () => {
    if (!selectedClass || !selectedSubject || !selectedPeriod || !selectedYear) return;
    
    const [term, type] = selectedPeriod.split('_');
    setLoading(true);
    setError(null);
    try {
      const data = await assessmentService.getResultAnalysis(
        parseInt(selectedClass, 10),
        parseInt(selectedSubject, 10),
        parseInt(term, 10),
        parseInt(type, 10),
        parseInt(selectedYear, 10)
      );
      setReport(data);
    } catch (err: any) {
      setError('Failed to aggregate result analysis report from active scores.');
    } finally {
      setLoading(false);
    }
  };

  // Auto trigger compilations on settings modifications
  useEffect(() => {
    fetchReport();
  }, [selectedClass, selectedSubject, selectedPeriod, selectedYear]);

  const handlePrint = () => {
    window.print();
  };

  const getSubjectName = (id: string) => {
    return subjectsList.find(s => String(s.id) === id)?.subject_name || 'Subject';
  };

  const getClassName = (id: string) => {
    return classesList.find(c => String(c.id) === id)?.class_name || 'Class';
  };

  const getYearName = (id: string) => {
    return academicYears.find(y => String(y.id) === id)?.year_name || 'Academic Year';
  };

  const getPeriodLabel = (val: string) => {
    switch (val) {
      case '1_1': return 'Term 1 Mid-Term';
      case '1_2': return 'End of Term 1';
      case '2_1': return 'Term 2 Mid-Term';
      case '2_2': return 'End of Term 2';
      case '3_1': return 'Term 3 Mid-Term';
      case '3_2': return 'End of Term 3';
      default: return 'Assessment Period';
    }
  };

  if (metaLoading) {
    return (
      <div className="flex h-60 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-xs font-semibold text-slate-500 text-helper">Loading report configurator...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print:p-0 print:m-0">
      
      {/* Dynamic print stylesheet overrides */}
      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          body {
            background: white !important;
            color: black !important;
          }
          header, aside, nav, button, .filter-drawer {
            display: none !important;
          }
          main {
            padding: 0 !important;
            background: none !important;
          }
          .print-container {
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
          }
          .print-header {
            display: block !important;
            margin-bottom: 24px;
            text-align: center;
          }
          .print-table {
            border: 1px solid #cbd5e1 !important;
          }
        }
        .print-header {
          display: none;
        }
      `}} />

      {/* 1. Header controls */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4 filter-drawer">
        <div>
          <h1 className="text-page-header mb-1">Diagnostic Result Reports</h1>
          <p className="text-helper">Live aggregates continuous assessment scores into gender performance breakdowns and pass rate percentages.</p>
        </div>
        <button 
          onClick={handlePrint} 
          className="bg-white hover:bg-slate-50 border border-indigo-600 text-indigo-600 font-semibold py-2.5 px-4 rounded-xl transition-all shadow-sm text-nav text-center whitespace-nowrap"
        >
          🖨️ Print Report Summary
        </button>
      </div>

      {/* Print-Only Roster Title Info */}
      <div className="print-header space-y-1.5">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">{tenantName || 'Institution Roster Portal'}</h1>
        <h2 className="text-sm font-semibold text-slate-600">Continuous Assessment Analytical Summary Sheet</h2>
        <p className="text-xs text-slate-500 font-medium">
          Class: {getClassName(selectedClass)} | Subject: {getSubjectName(selectedSubject)} | Session: {getPeriodLabel(selectedPeriod)} ({getYearName(selectedYear)})
        </p>
      </div>

      {/* 2. Parameters Drawer Filter */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm grid grid-cols-2 md:grid-cols-4 gap-4 filter-drawer">
        <div className="space-y-1">
          <label className="text-form-label uppercase tracking-wider block">Class</label>
          <select 
            value={selectedClass} 
            onChange={(e) => setSelectedClass(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-table-body outline-none focus:ring-1 focus:ring-indigo-500"
          >
            {classesList.map(c => <option key={c.id} value={String(c.id)}>{c.class_name}</option>)}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-form-label uppercase tracking-wider block">Subject</label>
          <select 
            value={selectedSubject} 
            onChange={(e) => setSelectedSubject(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-table-body outline-none focus:ring-1 focus:ring-indigo-500"
          >
            {subjectsList.map(s => <option key={s.id} value={String(s.id)}>{s.subject_name}</option>)}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-form-label uppercase tracking-wider block">Academic Year</label>
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-table-body outline-none focus:ring-1 focus:ring-indigo-500"
          >
            {academicYears.map(y => <option key={y.id} value={String(y.id)}>{y.year_name}</option>)}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-form-label uppercase tracking-wider block">Assessment Period</label>
          <select 
            value={selectedPeriod} 
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-table-body outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="1_1">Term 1 Mid-Term</option>
            <option value="1_2">End of Term 1</option>
            <option value="2_1">Term 2 Mid-Term</option>
            <option value="2_2">End of Term 2</option>
            <option value="3_1">Term 3 Mid-Term</option>
            <option value="3_2">End of Term 3</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-helper text-center">
          ⚠️ {error}
        </div>
      )}

      {/* 3. Aggregates compilation details */}
      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent mx-auto"></div>
            <p className="mt-4 text-xs font-semibold text-slate-500 text-helper">Compiling score-sheets analytics...</p>
          </div>
        </div>
      ) : report ? (
        <div className="space-y-6 print-container">
          
          {/* Stat Progress Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            
            {/* Boys aggregates */}
            <div className="bg-white border border-slate-200 border-l-4 border-l-indigo-600 rounded-2xl p-5 shadow-sm space-y-3.5 print:border-1 print:border-l-4">
              <h3 className="text-form-label uppercase tracking-wider text-slate-400">♂️ Boys Performance</h3>
              <p className="text-3xl font-extrabold text-slate-900 tracking-tight leading-none">{report.boys.pass_rate}%</p>
              
              <div className="space-y-1.5 text-helper text-slate-500 pt-1">
                <div className="flex justify-between">
                  <span>Sat Appraisal:</span>
                  <strong className="text-slate-800">{report.boys.sat} of {report.boys.enrolled}</strong>
                </div>
                <div className="flex justify-between">
                  <span>Passed (&ge;40%):</span>
                  <strong className="text-emerald-600">{report.boys.passed}</strong>
                </div>
                <div className="flex justify-between">
                  <span>Failed (&lt;40%):</span>
                  <strong className="text-rose-600">{report.boys.failed}</strong>
                </div>
              </div>
            </div>

            {/* Girls aggregates */}
            <div className="bg-white border border-slate-200 border-l-4 border-l-pink-600 rounded-2xl p-5 shadow-sm space-y-3.5 print:border-1 print:border-l-4">
              <h3 className="text-form-label uppercase tracking-wider text-slate-400">♀️ Girls Performance</h3>
              <p className="text-3xl font-extrabold text-slate-900 tracking-tight leading-none">{report.girls.pass_rate}%</p>
              
              <div className="space-y-1.5 text-helper text-slate-500 pt-1">
                <div className="flex justify-between">
                  <span>Sat Appraisal:</span>
                  <strong className="text-slate-800">{report.girls.sat} of {report.girls.enrolled}</strong>
                </div>
                <div className="flex justify-between">
                  <span>Passed (&ge;40%):</span>
                  <strong className="text-emerald-600">{report.girls.passed}</strong>
                </div>
                <div className="flex justify-between">
                  <span>Failed (&lt;40%):</span>
                  <strong className="text-rose-600">{report.girls.failed}</strong>
                </div>
              </div>
            </div>

            {/* Combined aggregates */}
            <div className="bg-white border border-slate-200 border-l-4 border-l-emerald-600 rounded-2xl p-5 shadow-sm space-y-3.5 print:border-1 print:border-l-4">
              <h3 className="text-form-label uppercase tracking-wider text-slate-400">👥 Global Summary</h3>
              <p className="text-3xl font-extrabold text-slate-900 tracking-tight leading-none">{report.totals.pass_rate}%</p>
              
              <div className="space-y-1.5 text-helper text-slate-500 pt-1">
                <div className="flex justify-between">
                  <span>Sat Appraisal:</span>
                  <strong className="text-slate-800">{report.totals.sat} of {report.totals.enrolled}</strong>
                </div>
                <div className="flex justify-between">
                  <span>Passed (&ge;40%):</span>
                  <strong className="text-emerald-600">{report.totals.passed}</strong>
                </div>
                <div className="flex justify-between">
                  <span>Failed (&lt;40%):</span>
                  <strong className="text-rose-600">{report.totals.failed}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Table summary sheet */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden print-table">
            <div className="p-4 border-b border-slate-100 filter-drawer">
              <h2 className="text-section-header">Aggregated Appraisal Statistics</h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="p-3.5 text-left text-card-title text-slate-400 uppercase tracking-wider">Cohort Category</th>
                    <th className="p-3.5 text-center text-card-title text-slate-400 uppercase tracking-wider">Enrolled</th>
                    <th className="p-3.5 text-center text-card-title text-slate-400 uppercase tracking-wider">Sat Assessment</th>
                    <th className="p-3.5 text-center text-card-title text-slate-400 uppercase tracking-wider text-emerald-600">Passed (&ge;40.00)</th>
                    <th className="p-3.5 text-center text-card-title text-slate-400 uppercase tracking-wider text-rose-600">Failed (&lt;40.00)</th>
                    <th className="p-3.5 text-center text-card-title text-slate-400 uppercase tracking-wider">Pass Rate Percentage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr className="hover:bg-slate-50/45 transition-colors">
                    <td className="p-3.5 text-table-body font-bold text-slate-900">Male Pupils (Boys)</td>
                    <td className="p-3.5 text-center text-table-body font-semibold">{report.boys.enrolled}</td>
                    <td className="p-3.5 text-center text-table-body">{report.boys.sat}</td>
                    <td className="p-3.5 text-center text-table-body text-emerald-600 font-bold">{report.boys.passed}</td>
                    <td className="p-3.5 text-center text-table-body text-rose-650 font-bold">{report.boys.failed}</td>
                    <td className="p-3.5 text-center text-table-body font-bold text-indigo-600">{report.boys.pass_rate}%</td>
                  </tr>
                  <tr className="hover:bg-slate-50/45 transition-colors">
                    <td className="p-3.5 text-table-body font-bold text-slate-900">Female Pupils (Girls)</td>
                    <td className="p-3.5 text-center text-table-body font-semibold">{report.girls.enrolled}</td>
                    <td className="p-3.5 text-center text-table-body">{report.girls.sat}</td>
                    <td className="p-3.5 text-center text-table-body text-emerald-600 font-bold">{report.girls.passed}</td>
                    <td className="p-3.5 text-center text-table-body text-rose-650 font-bold">{report.girls.failed}</td>
                    <td className="p-3.5 text-center text-table-body font-bold text-indigo-600">{report.girls.pass_rate}%</td>
                  </tr>
                  <tr className="bg-slate-50 border-t-2 border-slate-200">
                    <td className="p-3.5 text-table-body font-extrabold text-indigo-955">Combined Totals</td>
                    <td className="p-3.5 text-center text-table-body font-extrabold text-slate-900">{report.totals.enrolled}</td>
                    <td className="p-3.5 text-center text-table-body font-extrabold text-slate-900">{report.totals.sat}</td>
                    <td className="p-3.5 text-center text-table-body text-emerald-700 font-extrabold">{report.totals.passed}</td>
                    <td className="p-3.5 text-center text-table-body text-rose-700 font-extrabold">{report.totals.failed}</td>
                    <td className="p-3.5 text-center text-table-body font-extrabold text-indigo-650 text-xs">{report.totals.pass_rate}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center text-helper italic text-slate-400">
          Select parameters above to calculate results.
        </div>
      )}

    </div>
  );
};

export default Reports;
